/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useContext, useState } from 'react';
import { Button, Col, Row } from '@thd-olt-component-react/core-ui';
import { QueryContext, QueryProvider } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  DrawerHeader, Drawer, DrawerBody, DrawerFooter, RatingMeter, Typography
} from '@one-thd/sui-atomic-components';
import {
  ProductPod, ProductBadge, ProductImage, PodSticker
} from '@thd-olt-component-react/product-pod';
import { Price } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { SwapItem } from '@thd-olt-component-react/swap-item';
import {
  func, bool, string, arrayOf, shape
} from 'prop-types';
import { QuickViewWrapper } from '../QuickViewWrapper/QuickViewWrapper';
import { ProductSetContext } from '../../context/ProductSetContext';
import { REMOVE_HEADER } from '../../constants';
import './remove-drawer.scss';

export const RemoveBtnDrawer = ({
  closeDrawer,
  itemId,
  isDrawerOpen,
  productGroup,
  products
}) => {
  const { swapGroup, removeGroup, storeId } = useContext(ProductSetContext);
  const { defaultVariables } = useContext(QueryContext) || {};
  const { averageRating, totalReviews } = products?.[0]?.reviews?.ratingsReviews || {};
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const openQuickView = (event) => {
    event.preventDefault();
    setQuickViewOpen(true);
  };

  const handleSwap = (swappedItem) => {
    swapGroup([swappedItem.product], productGroup);
    closeDrawer();
  };

  const newDefaultVariables = {
    ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean' && {
      isBrandPricingPolicyCompliant: defaultVariables.current.isBrandPricingPolicyCompliant
    })
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={() => closeDrawer()}
    >
      <div className="sui-shadow-md sui-mb-2">
        <DrawerHeader title={REMOVE_HEADER} onClose={() => closeDrawer()} />
      </div>
      <DrawerBody className="sui-h-full sui-px-4 sui-pt-2 sui-pb-4">
        <div className="sui-h-auto" data-testid="remove-drawer">
          <Typography height="tight" variant="h5">Current Selection</Typography>
          <ProductPod
            itemId={itemId}
            storeId={storeId}
            analyticsData={{ parent: 'product-set', position: 1 }}
            render={({ itemId: podItemId = '' }) => (
              <div className="sui-row sui-flex">
                <PodSticker position="top-left">
                  <ProductBadge itemId={itemId} storeId={storeId} />
                </PodSticker>
                <div className="sui-col">
                  <div className="sui-flex sui-w-20 sui-h-20 sui-pr-2">
                    <ProductImage
                      itemId={itemId}
                      onClick={openQuickView}
                      skeletonSize={30}
                    />
                  </div>
                </div>
                <div className="sui-col sui-w-full sui-px-2 sui-mr-2">
                  <ProductHeader
                    brand="above"
                    itemId={itemId}
                    brandTitleMaxLine={4}
                    brandFontSize="base"
                    productNameFontSize="base"
                    brandLineHeight="normal"
                    productNameLineHeight="normal"
                    onClick={openQuickView}
                    data-testid="product-header"
                  />
                  <div className="sui-flex sui-items-center sui-h-6 sui-mb-3">
                    <QuickViewWrapper
                      itemId={itemId}
                      isQuickViewOpen={quickViewOpen}
                      onQuickViewClose={() => setQuickViewOpen(false)}
                    >
                      <button
                        type="button"
                        onClick={openQuickView}
                        data-testid="ratings-button"
                        style={{ all: 'unset', cursor: 'pointer' }}
                      >
                        <RatingMeter
                          value={parseFloat(averageRating)}
                          label={`(${totalReviews})`}
                          edge="start"
                          RatingProps={{
                            color: 'brand'
                          }}
                        />
                      </button>
                    </QuickViewWrapper>
                  </div>
                  <Price
                    itemId={podItemId}
                    large={false}
                    storeId={storeId}
                    displayEachUom={false}
                  />
                </div>
              </div>
            )}
          />
        </div>
      </DrawerBody>
      <div className="sui-shadow-md">
        <DrawerFooter variant="sticky">
          <div className="sui-flex gui-gap-2">
            <Row>
              {isMobile ? (
                <div className="sui-w-full">
                  <Col fallback="12">
                    <Button onClick={() => {
                      removeGroup(productGroup);
                      closeDrawer();
                    }}
                    >
                      Remove
                    </Button>
                  </Col>
                  <Col fallback="12">
                    <QueryProvider
                      cacheKey="productSet-remove-drawer"
                      defaultVariables={newDefaultVariables}
                    >
                      <SwapItem
                        itemId={itemId}
                        className="product-set__remove-swap-button"
                        onSwap={handleSwap}
                        isDark={false}
                        hideIcon
                        btnContent="Swap for Similar"
                        closeOnSwap
                      />
                    </QueryProvider>
                  </Col>
                </div>
              ) : (
                <>
                  <Col fallback="6">
                    <QueryProvider
                      cacheKey="productSet-remove-drawer"
                      defaultVariables={newDefaultVariables}
                    >
                      <SwapItem
                        itemId={itemId}
                        className="product-set__remove-swap-button"
                        onSwap={handleSwap}
                        isDark={false}
                        hideIcon
                        btnContent="Swap for Similar"
                        closeOnSwap
                      />
                    </QueryProvider>
                  </Col>
                  <Col fallback="6">
                    <Button onClick={() => {
                      removeGroup(productGroup);
                      closeDrawer();
                    }}
                    >
                      Remove
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </div>
        </DrawerFooter>
      </div>
    </Drawer>
  );
};

RemoveBtnDrawer.propTypes = {
  closeDrawer: func.isRequired,
  itemId: string.isRequired,
  isDrawerOpen: bool.isRequired,
  productGroup: string.isRequired,
  products: arrayOf(shape({})).isRequired,
};
